import React, { ReactNode, useEffect, useState } from 'react';
import Footer from '../../components/Common/Footer';
import { Link, useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import MainTeacherControlBar from './components/MainTeacherControlBar';
import { getTeacherClassroomProjects, getTeacherClassrooms } from '../../store/thunks/user-teacher.thunk';
import NewDatePickerPopup, { DateSelectorMode } from './components/NewDatePickerPopup';
import { SelectedDateTime } from './components/PublishingTimelineForm';
import {
  setActiveClassroomProject,
  setActiveClassroomProjects,
  setIsOnboarding,
  setNewClassroomFlow,
  setSelectProjectById,
  setShowAddNewProject,
  setShowAddNewStudentsPopup,
  setShowPublishingTimelinePopup,
  setShowStartTourPopup,
  setTeacherOnboarding,
  setTeacherTour,
} from '../../store/userSlice';
import {
  createUserClassroomProject,
  saveProjectTimeline,
  saveProjectTourProgress,
  saveTeacherFinalSurvey,
  UpdateProjectTimelineSourceEnum,
} from './services/Teacher.service';
import { ProjectModel } from '../../interfaces/project.interface';
import { convertTimestampToDateTimeString } from '../../helper/helper';
import ClassroomAddNewProject from './components/ClassroomAddNewProject';
import Modal from '../../components/Common/modal/Modal';
import { teacherTour as teacherTourUtil } from './utils/teacherTour';
import OnboardingFinalSurvey from './components/OnboardingFinalSurvey';
import { FinalSurveyResponses, TeacherOnboardingModel } from '../../interfaces/teacher.interface';
import { TeacherOnboardingStatusEnum } from '../../interfaces/user.interface';
import OnboardingSkipFinalOnboarding from './components/OnboardingSkipFinalOnboarding';
import OnboardingCompleteOnboarding from './components/OnboardingCompleteOnboarding';
import OnboardingQuickStartTour from './components/OnboardingQuickStartTour';
import OnboardingStartTour from './components/OnboardingStartTour';
import { getOnboardingData } from './services/Teacher.service';
import { getOnboardingResumeRoute } from './utils/onboarding.utils';

const Teacher = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const currentSearchQuery = useSelector((state: RootState) => state.app.currentSearchQuery);
  const isOnboarding = useSelector((state: RootState) => state.user.isOnboarding);
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);
  const activeClassroomProject = useSelector((state: RootState) => state.user.activeClassroomProject);
  const activeClassroomProjects = useSelector((state: RootState) => state.user.activeClassroomProjects);
  const isNewClassroomFlow = useSelector((state: RootState) => state.user.isNewClassroomFlow);
  const showPublishingTimelinePopup = useSelector((state: RootState) => state.user.showPublishingTimelinePopup);
  const showAddNewProject = useSelector((state: RootState) => state.user.showAddNewProject);
  const teacherTourState = useSelector((state: RootState) => state.user.teacherTour);
  const currentPathname = useSelector((state: RootState) => state.app.currentPathname);
  const [selectedProject, setSelectedProject] = useState<ProjectModel | null>(null);
  const [saveTimelineLoading, setSaveTimelineLoading] = useState<boolean>(false);
  const [showFinalSurvey, setShowFinalSurvey] = useState<boolean>(false);
  const [showSkipFinalOnboardingPopup, setShowSkipFinalOnboardingPopup] = useState<boolean>(false);
  const [showCompleteOnboardingPopup, setShowCompleteOnboardingPopup] = useState<boolean>(false);
  const showStartTourPopup = useSelector((state: RootState) => state.user.showStartTourPopup);

  const [addNewProjectLoader, setAddNewProjectLoader] = useState<boolean>(false);
  const [showFinalSurveyLoader, setShowFinalSurveyLoader] = useState<boolean>(false);

  useEffect(() => {
    if (teacherTourState?.show) {
      const onSuccess = (isSkip?: boolean) => {
        if (isOnboarding) {
          saveProjectTourProgress(activeClassroom?.id, activeClassroomProject?.id, 'finish', !isSkip).then().catch();
        }
        isOnboarding
          ? navigateTo(`/classroom/dashboard?onboarding=${isOnboarding}&final=true`)
          : navigateTo(`/classroom/dashboard`);
      };
      const openStep = teacherTourState?.step > 1 ? teacherTourState.step : undefined;
      teacherTourUtil.start(navigateTo, onSuccess, openStep, !!isOnboarding);
    }
  }, [teacherTourState]);

  useEffect(() => {
    if (teacherTourState?.show) {
      teacherTourUtil.refreshTour();
    }
  }, [currentPathname]);

  useEffect(() => {
    dispatch(setTeacherTour({ show: false, step: 1 }));
    dispatch(getTeacherClassrooms());
    if (!teacherOnboarding) {
      getOnboardingData(dispatch);
    }

    return () => {
      dispatch(setActiveClassroomProject({ project: null }));
      dispatch(setActiveClassroomProjects({ projects: null }));
      dispatch(setTeacherTour({ show: false, step: 1 }));
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.teacherOnboardingStatus === TeacherOnboardingStatusEnum.BookOnboarding) {
      navigateTo('/', { replace: true });
    } else if (user.teacherOnboardingStatus === TeacherOnboardingStatusEnum.TeacherInfo) {
      navigateTo(getOnboardingResumeRoute(teacherOnboarding as TeacherOnboardingModel), { replace: true });
    }
  }, [user?.teacherOnboardingStatus, teacherOnboarding]);

  useEffect(() => {
    const queryParams = new URLSearchParams(currentSearchQuery);
    const isOnboarding = queryParams.get('onboarding') === 'true';
    dispatch(setIsOnboarding(isOnboarding));
    if (activeClassroomProject?.id) {
      // if (isOnboarding) {
      //   dispatch(
      //     setShowPublishingTimelinePopup(
      //       queryParams.get('showCelebration') === 'true'
      //         ? { projectId: activeClassroomProject?.id, openAddStudentsPopup: isOnboarding }
      //         : null,
      //     ),
      //   );
      // }
      dispatch(setShowAddNewStudentsPopup(queryParams.get('addStudents') === 'true'));
      setShowFinalSurvey(queryParams.get('final') === 'true');
      setShowSkipFinalOnboardingPopup(queryParams.get('skip') === 'true');
      setShowCompleteOnboardingPopup(queryParams.get('complete') === 'true');
      if (queryParams.get('addStartDate') === 'true') {
        dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject?.id, openAddStudentsPopup: true }));
      }
    }
  }, [activeClassroomProject, currentSearchQuery]);

  useEffect(() => {
    if (activeClassroomProject?.id) {
      if (isNewClassroomFlow) {
        dispatch(setShowPublishingTimelinePopup({ projectId: activeClassroomProject?.id, openAddStudentsPopup: true }));
        dispatch(setNewClassroomFlow(false));
      }
    }
  }, [activeClassroomProject]);

  useEffect(() => {
    const projectId = showPublishingTimelinePopup?.projectId;
    if (projectId) {
      const fromList = activeClassroomProjects?.find((p) => p.id === projectId);
      const fromActive = activeClassroomProject?.id === projectId ? activeClassroomProject : null;
      const source = fromList || fromActive;
      if (source) {
        setSelectedProject({
          id: source.id,
          startDate: source.startDate,
          startDateTime: source.startDateTime,
        });
      } else {
        setSelectedProject({
          id: projectId,
          startDate: undefined,
          startDateTime: undefined,
        });
      }
    } else {
      setSelectedProject(null);
    }
  }, [showPublishingTimelinePopup?.projectId, activeClassroomProject, activeClassroomProjects]);

  const handleSaveTimeline = async (data: SelectedDateTime, mode: DateSelectorMode) => {
    setSaveTimelineLoading(true);
    try {
      await saveProjectTimeline(
        activeClassroom?.id,
        showPublishingTimelinePopup?.projectId,
        data,
        mode,
        () => {
          dispatch(setShowPublishingTimelinePopup(null));
          if (showPublishingTimelinePopup?.openAddStudentsPopup) {
            if (isOnboarding) {
              navigateTo(`/classroom/students?onboarding=${isOnboarding}&addStudents=true`);
            } else {
              navigateTo(`/classroom/students?onboarding=${isOnboarding}`);
            }
            dispatch(setShowAddNewStudentsPopup(true));
          }

          dispatch(getTeacherClassroomProjects(activeClassroom?.id!));
        },
        isOnboarding ? UpdateProjectTimelineSourceEnum.CREATING_FLOW : UpdateProjectTimelineSourceEnum.SETTINGS,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => setSaveTimelineLoading(false));
    }
  };

  const createNewProject = async (projectName: string, projectTemplateId?: string) => {
    if (!activeClassroom?.id) {
      return;
    }
    try {
      setAddNewProjectLoader(true);
      await createUserClassroomProject(
        activeClassroom?.id,
        projectName,
        (response) => {
          if (response.project) {
            dispatch(setShowPublishingTimelinePopup({ projectId: response.project.id }));
          }
          dispatch(setSelectProjectById(response.project.id));
          dispatch(setShowAddNewProject(false));
          dispatch(getTeacherClassroomProjects(activeClassroom?.id!));
        },
        { projectTemplateId },
      );
    } catch (error) {
      console.error(error);
    } finally {
      setAddNewProjectLoader(false);
    }
  };

  // Handle final survey submission
  const handleFinalSurveySubmit = async (responses: FinalSurveyResponses | null, isSkip?: boolean) => {
    !isSkip && setShowFinalSurveyLoader(true);
    const updatedOnboarding: Partial<TeacherOnboardingModel> = {
      finalSurveyCompleted: !!responses,
      finalSurveySkipped: !!isSkip,
      // Store survey responses in the onboarding data
      finalSurveyResponses: responses,
    };

    try {
      if (isSkip) {
        saveTeacherFinalSurvey(
          updatedOnboarding,
          () => {
            dispatch(setTeacherOnboarding({ data: updatedOnboarding }));
            setShowFinalSurvey(false);
          },
          true,
        )
          .then()
          .catch();
      } else {
        await saveTeacherFinalSurvey(updatedOnboarding, () => {
          dispatch(setTeacherOnboarding({ data: updatedOnboarding }));
          setShowFinalSurvey(false);
        });
      }
    } catch (error) {
      console.error('Failed to save final survey:', error);
    } finally {
      setShowFinalSurveyLoader(false);
      if (isSkip) {
        setShowFinalSurvey(false);
      }
      navigateTo(`/classroom/dashboard?onboarding=${isOnboarding}&${isSkip ? 'skip=true' : 'complete=true'}`);
    }
  };

  const handleFinalSurveyClose = () => {
    setShowFinalSurvey(false);
  };

  const publishingTimelineDescription = () => {
    return (
      <span>
        Select your class start date below. This helps us remind you when to send home flyers and when to send your
        books to print. You can update your date anytime. Want an in depth timeline? View the{' '}
        <Link
          to="https://buildmystory.com/resources/"
          target="_blank"
          rel="noopener noreferrer"
          className={'text-secondaryStoryAdditionalLightBlueText underline'}
        >
          Step-by-Step Guide
        </Link>
        .
      </span>
    );
  };

  return (
    <div className="App">
      <NavigationBar />
      <div className="bg-storytan mx-auto teacher-main-tour-container">
        <MainTeacherControlBar user={user}></MainTeacherControlBar>
        <div className="relative">{children}</div>
      </div>
      <Footer />
      <NewDatePickerPopup
        isOpen={!!showPublishingTimelinePopup?.projectId}
        isMonthSelection={isOnboarding}
        showTopRightCloseButton={!isOnboarding}
        onClose={
          isOnboarding
            ? undefined
            : () => {
                dispatch(setShowPublishingTimelinePopup(null));
                // Skip navigation if user cancels during onboarding
                if (isOnboarding && showPublishingTimelinePopup?.openAddStudentsPopup) {
                  handleSaveTimeline({}, 'date')
                    .then(() => {
                      if (isOnboarding) {
                        navigateTo(`/classroom/students?onboarding=${isOnboarding}&addStudents=true`);
                      } else {
                        navigateTo(`/classroom/students?onboarding=${isOnboarding}`);
                      }
                      dispatch(setShowAddNewStudentsPopup(true));
                    })
                    .catch(console.error);
                }
              }
        }
        onDateSelected={handleSaveTimeline}
        selectedDate={
          selectedProject?.startDate && new Date(convertTimestampToDateTimeString(selectedProject.startDate))
        }
        isLoading={saveTimelineLoading}
      />

      {showAddNewProject && (
        <Modal
          title={`Create a new Project`}
          description={`Create a new project so you can run multiple programs throughout the year for the same class.`}
          className={'global-absolute-popup-style font-sofiaSans content-left secondary-small'}
          mainContainerClassName={'sm:max-w-[430px]'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={false}
        >
          <ClassroomAddNewProject
            loader={addNewProjectLoader}
            close={() => dispatch(setShowAddNewProject(false))}
            submit={(name: string, projectTemplateId?: string) => createNewProject(name, projectTemplateId)}
          ></ClassroomAddNewProject>
        </Modal>
      )}

      {/* Final Onboarding Survey */}
      {showFinalSurvey && (
        <Modal
          title={`Hi ${user?.firstName || 'teacher'}, let's make sure you're ready before your class begins 🚀`}
          className={'global-absolute-popup-style font-sofiaSans content-left'}
          mainContainerClassName={'sm:max-w-[500px] secondary-small-popup p-4 md:p-8 px-4 sm:px-4 lg:px-4'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          mainTitleContainerClassName={'mb-1 px-1 sm:px-3 lg:px-6 pt-2 sm:pt-1 md:pt-0'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => handleFinalSurveySubmit(null, true)}
        >
          <OnboardingFinalSurvey
            onClose={handleFinalSurveyClose}
            onSubmit={handleFinalSurveySubmit}
            onBack={() => {
              setShowFinalSurvey(false);
              dispatch(setTeacherTour({ show: true, step: 9 }));
            }}
            onSkip={() => handleFinalSurveySubmit(null, true)}
            loader={showFinalSurveyLoader}
          />
        </Modal>
      )}

      {showSkipFinalOnboardingPopup && (
        <Modal
          title={''}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[550px]'}
          showSubmitButton={false}
          showCloseButton={false}
        >
          <OnboardingSkipFinalOnboarding
            submit={() => {
              setShowSkipFinalOnboardingPopup(false);
              navigateTo(`/classroom/dashboard`);
            }}
            onBack={() => {
              setShowSkipFinalOnboardingPopup(false);
              isOnboarding
                ? navigateTo(`/classroom/dashboard?onboarding=${isOnboarding}&final=true`)
                : navigateTo(`/classroom/dashboard`);
            }}
          ></OnboardingSkipFinalOnboarding>
        </Modal>
      )}

      {showCompleteOnboardingPopup && (
        <Modal
          title={''}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[550px]'}
          showSubmitButton={false}
          showCloseButton={false}
        >
          <OnboardingCompleteOnboarding
            submit={() => {
              setShowCompleteOnboardingPopup(false);
              navigateTo(`/classroom/dashboard`);
            }}
            onBack={() => {
              setShowCompleteOnboardingPopup(false);
              isOnboarding
                ? navigateTo(`/classroom/dashboard?onboarding=${isOnboarding}&final=true`)
                : navigateTo(`/classroom/dashboard`);
            }}
          ></OnboardingCompleteOnboarding>
        </Modal>
      )}
      {showStartTourPopup && (
        <Modal
          title={''}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[550px]'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => dispatch(setShowStartTourPopup(false))}
        >
          {isOnboarding ? (
            <OnboardingStartTour
              submit={() => {
                dispatch(setShowStartTourPopup(false));
                dispatch(setTeacherTour({ show: true, step: 1 }));
                navigateTo(`/classroom/students?onboarding=${isOnboarding}`);
                saveProjectTourProgress(activeClassroom?.id, activeClassroomProject?.id, 'start', true).then().catch();
              }}
              skip={() => {
                dispatch(setShowStartTourPopup(false));
                navigateTo(`/classroom/dashboard?onboarding=${isOnboarding}&final=true`);
                saveProjectTourProgress(activeClassroom?.id, activeClassroomProject?.id, 'start', false).then().catch();
              }}
              onBack={() => {
                dispatch(setShowStartTourPopup(false));
                navigateTo(`/classroom/students?onboarding=${isOnboarding}&addStudents=true`);
              }}
            ></OnboardingStartTour>
          ) : (
            <OnboardingQuickStartTour
              submit={() => {
                dispatch(setShowStartTourPopup(false));
                dispatch(setTeacherTour({ show: true, step: 1 }));
                navigateTo('/classroom/students');
              }}
            ></OnboardingQuickStartTour>
          )}
        </Modal>
      )}
    </div>
  );
};

export default Teacher;
